import type {
  ProductionOrderStatus,
  ProductionRequestStatus,
  ProductionTaskKind,
  ProductionTaskStatus,
} from '@warehouse/shared';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'brand' | 'accent' | 'info';

/**
 * Domain status → badge colour, in one place.
 *
 * Colour is meaning here, not decoration: on a board the operator reads the
 * pill before the word, and a status that changes hue between two screens is a
 * status they stop trusting. Keeping the maps pure also makes them the cheapest
 * thing in the module to test.
 */

export const PRODUCTION_ORDER_TONES: Readonly<Record<ProductionOrderStatus, BadgeTone>> = {
  planned: 'neutral',
  released: 'info',
  in_progress: 'warning',
  done: 'success',
  cancelled: 'danger',
};

export const PRODUCTION_TASK_TONES: Readonly<Record<ProductionTaskStatus, BadgeTone>> = {
  open: 'neutral',
  in_progress: 'warning',
  done: 'success',
};

export const PRODUCTION_REQUEST_TONES: Readonly<Record<ProductionRequestStatus, BadgeTone>> = {
  requested: 'info',
  accepted: 'warning',
  delivered: 'success',
  rejected: 'danger',
};

export const PRODUCTION_TASK_KIND_TONES: Readonly<Record<ProductionTaskKind, BadgeTone>> = {
  unload: 'accent',
  move_to_production: 'brand',
  general: 'neutral',
};

/** Board/kanban column order — the operator's left-to-right reading order. */
export const TASK_COLUMNS: readonly ProductionTaskStatus[] = ['open', 'in_progress', 'done'];
export const REQUEST_COLUMNS: readonly ProductionRequestStatus[] = [
  'requested',
  'accepted',
  'delivered',
  'rejected',
];

/** Which buttons an order shows, derived from its status — never hardcoded per page. */
export interface OrderActions {
  canEdit: boolean;
  canRelease: boolean;
  canStart: boolean;
  canComplete: boolean;
  canCancel: boolean;
  canPlanStaging: boolean;
  canStage: boolean;
}

export function orderActionsFor(status: ProductionOrderStatus): OrderActions {
  const live = status === 'planned' || status === 'released' || status === 'in_progress';
  return {
    canEdit: live,
    canRelease: status === 'planned',
    canStart: status === 'released',
    canComplete: status === 'released' || status === 'in_progress',
    canCancel: live,
    canPlanStaging: live,
    canStage: live,
  };
}

/** A task is late once its due time has passed and it is not finished. */
export function isOverdue(
  task: { dueAt: string | null; status: ProductionTaskStatus },
  now: Date = new Date(),
): boolean {
  if (!task.dueAt || task.status === 'done') return false;
  const due = new Date(task.dueAt);
  return !Number.isNaN(due.getTime()) && due.getTime() < now.getTime();
}

/** Completion share, clamped to 0–100 so a 120% over-report cannot break a bar. */
export function producedPercent(qtyBase: string, producedQtyBase: string): number {
  const total = Number(qtyBase);
  const produced = Number(producedQtyBase);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(produced) || produced <= 0) return 0;
  return Math.min(100, Math.round((produced / total) * 100));
}
